import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { GraduationCap } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * /try/:slug
 *
 * Magic-link entry point. No login UI. We POST to /api/invites/resolve/:slug,
 * the backend mints a JWT and sets cookies, then we refresh auth state and
 * land the visitor directly inside the app.
 *
 * Multi-device safe: the same link opened on phone + laptop creates two
 * distinct guest user records (so we get clean per-device intel).
 */
export default function InviteLanding() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [error, setError] = useState('');
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    (async () => {
      try {
        const { data } = await axios.get(`${API}/invites/resolve/${encodeURIComponent(slug)}`);

        // Pull the freshly-authed user
        const me = await axios.get(`${API}/auth/me`);
        setUser(me.data);

        // First-time visitors get the onboarding overlay; the tour listens for this flag.
        // Also force the Browse Home tab to School Year so the tour's grade-4 anchor is visible.
        if (data.first_time) {
          sessionStorage.setItem('invite_show_onboarding', '1');
          localStorage.setItem('browseMode', 'school');
        }

        navigate(data.redirect || '/browse', { replace: true });
      } catch (e) {
        const detail = e?.response?.data?.detail;
        if (e?.response?.status === 404) setError('This link is not valid.');
        else if (e?.response?.status === 410) setError(detail || 'This link has expired.');
        else setError(detail || "We couldn't open the app right now. Please try again in a moment.");
      }
    })();
  }, [slug, navigate, setUser]);

  if (error) {
    return (
      <div data-testid="invite-error-screen" style={wrap}>
        <div style={card}>
          <div style={logo}><GraduationCap size={28} color="white" /></div>
          <h1 style={h1}>Hmm, something's off</h1>
          <p style={body}>{error}</p>
          <p style={muted}>If you received this link by email, reply and we'll send a fresh one.</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="invite-loading-screen" style={wrap}>
      <div style={card}>
        <div style={logo}><GraduationCap size={28} color="white" /></div>
        <div style={{ ...spinner, marginBottom: '1.25rem' }} />
        <p style={muted}>Opening the platform...</p>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    </div>
  );
}

const wrap = {
  height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
  background: '#F8F9FB',
};
const card = { textAlign: 'center', maxWidth: 420, padding: '2.5rem 2rem' };
const logo = {
  width: 56, height: 56, borderRadius: '0.875rem', background: '#1E2140',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  margin: '0 auto 1.75rem',
};
const h1 = { fontFamily: 'Outfit, sans-serif', fontSize: '1.5rem', fontWeight: 700, color: '#1A1D20', marginBottom: '0.625rem' };
const body = { fontFamily: 'Nunito, sans-serif', fontSize: '0.95rem', color: '#475569', lineHeight: 1.7, marginBottom: '0.75rem' };
const muted = { fontFamily: 'Nunito, sans-serif', fontSize: '0.85rem', color: '#94A3B8' };
const spinner = {
  width: 32, height: 32, border: '3px solid #E2E8F0', borderTopColor: '#1E2140',
  borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto',
};
